import {clsx, type ClassValue} from 'clsx';

type StyleProps = Record<string, unknown>;

type CompoundStyle = {
    readonly when: Record<string, string | boolean | number>;
    readonly class: ClassValue;
};

type StylePart = {
    readonly base?: ClassValue;
    readonly variants?: Record<string, Record<string, ClassValue>>;
    readonly defaults?: Record<string, string | boolean | number>;
    readonly compounds?: readonly CompoundStyle[];
};

type StyleDefinition = {
    readonly component: string;
    readonly parts: Record<string, StylePart>;
};

type DefineStylesOptions<T extends StyleDefinition> = {
    definition: T;
};

type ResolveStyleOptions = {
    part: StylePart;
    props?: StyleProps;
    className?: ClassValue;
};

const defineStyles = <const T extends StyleDefinition>({definition}: DefineStylesOptions<T>): Readonly<T> => Object.freeze(definition);

const normalizeStyleValue = (value: unknown): string => {
    if (value === true) return 'true';
    if (value === false || value === null || value === undefined) return 'false';
    return String(value);
};

const getStyleValue = (part: StylePart, props: StyleProps, axis: string): string => (
    normalizeStyleValue(Object.prototype.hasOwnProperty.call(props, axis) ? props[axis] : part.defaults?.[axis])
);

const compoundMatches = (
    part: StylePart,
    props: StyleProps,
    compound: CompoundStyle,
): boolean => Object.entries(compound.when).every(([axis, expected]) => (
    getStyleValue(part, props, axis) === normalizeStyleValue(expected)
));

const resolveStyle = ({
    part,
    props = {},
    className: override,
}: ResolveStyleOptions): string => {
    const classes: ClassValue[] = [part.base];

    Object.entries(part.variants ?? {}).forEach(([axis, variants]) => {
        classes.push(variants[getStyleValue(part, props, axis)]);
    });

    part.compounds?.forEach((compound) => {
        if (compoundMatches(part, props, compound)) {
            classes.push(compound.class);
        }
    });

    classes.push(override);

    return clsx(classes);
};

export {defineStyles, resolveStyle};
export type {CompoundStyle, DefineStylesOptions, ResolveStyleOptions, StyleDefinition, StylePart, StyleProps};
